/**
 * Turning the AI gateway's 429 into something a user can act on.
 *
 * The gateway refuses a call for one of two policy reasons — the tenant is out of
 * monthly/daily tokens, or too many requests came in this minute. Both arrive as
 * HTTP 429 with a structured body:
 *
 *   { "detail": { "error": "quota_exceeded" | "rate_limited",
 *                 "window"?: "month" | "day",       // quota only
 *                 "scope"?:  "tenant" | "user",     // rate limit only
 *                 "used"?, "limit"?, "limit_per_min"?,
 *                 "retry_after": <seconds>, "detail": "<human sentence>" } }
 *
 * Callers that stream (SSE) must check `response.ok` BEFORE reading the body: a
 * 429 body is JSON, so a reader looking for `data:` lines finds nothing and the
 * UI ends up rendering an empty message — which is exactly the bug this module
 * exists to prevent.
 *
 * Backend counterparts: inference_service/quota_guard.py, rate_limit.py.
 * For service-to-service callers the equivalent is the `error` key that
 * shared/inference_client.py returns.
 */

export interface AiLimitInfo {
  kind: 'quota_exceeded' | 'rate_limited' | 'unknown';
  window?: 'month' | 'day';
  scope?: 'tenant' | 'user';
  used?: number;
  limit?: number;
  retryAfter?: number;
  /** The sentence the backend produced. Kept as a fallback and for tooltips. */
  detail?: string;
}

/** True when the gateway refused this call on policy grounds. */
export const isAiLimited = (res: Response): boolean => res.status === 429;

/** Read a 429 body. Never throws — a malformed payload still yields a usable shape. */
export async function parseAiLimit(res: Response): Promise<AiLimitInfo> {
  const info: AiLimitInfo = { kind: 'unknown' };

  const header = res.headers.get('Retry-After');
  if (header) {
    const n = Number(header);
    if (Number.isFinite(n) && n > 0) info.retryAfter = n;
  }

  try {
    const body = await res.json();
    const d = body?.detail;
    if (typeof d === 'string') {
      info.detail = d;
    } else if (d && typeof d === 'object') {
      if (d.error === 'quota_exceeded' || d.error === 'rate_limited') info.kind = d.error;
      if (d.window === 'month' || d.window === 'day') info.window = d.window;
      if (d.scope === 'tenant' || d.scope === 'user') info.scope = d.scope;
      if (typeof d.used === 'number') info.used = d.used;
      // Quotas report `limit`; the rate limiter reports `limit_per_min`.
      if (typeof d.limit === 'number') info.limit = d.limit;
      else if (typeof d.limit_per_min === 'number') info.limit = d.limit_per_min;
      if (typeof d.retry_after === 'number' && d.retry_after > 0) info.retryAfter = d.retry_after;
      if (typeof d.detail === 'string') info.detail = d.detail;
    }
  } catch {
    /* keep whatever the headers gave us */
  }

  return info;
}

const fmtNum = (n: number) => new Intl.NumberFormat().format(n);

/** "in 45 seconds" / "in 12 minutes" / a concrete local date for long waits. */
function whenBack(seconds?: number): string {
  if (!seconds || seconds <= 0) return '';
  if (seconds < 90) return `in ${Math.ceil(seconds)} seconds`;
  if (seconds < 3600) return `in ${Math.ceil(seconds / 60)} minutes`;
  // Hours or days out — a wall-clock time is more useful than "in 56 hours".
  const at = new Date(Date.now() + seconds * 1000);
  const sameDay = at.toDateString() === new Date().toDateString();
  return sameDay
    ? `at ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : `on ${at.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** Short label for the notice header. */
export function aiLimitTitle(info: AiLimitInfo): string {
  if (info.kind === 'rate_limited') return 'Too many requests';
  if (info.kind === 'quota_exceeded') {
    return info.window === 'day' ? 'Daily AI limit reached' : 'Monthly AI limit reached';
  }
  return 'AI limit reached';
}

/**
 * The body of the notice. Says what ran out, how much was used, when it comes
 * back, and who can lift it — a bare "limit reached" leaves the user stuck.
 */
export function aiLimitMessage(info: AiLimitInfo): string {
  const back = whenBack(info.retryAfter);

  if (info.kind === 'rate_limited') {
    const who = info.scope === 'tenant' ? 'your organization' : 'you';
    const rate = info.limit ? ` The cap is ${fmtNum(info.limit)} requests per minute for ${who}.` : '';
    return `You sent messages faster than the AI can be called.${rate}` +
      (back ? ` Try again ${back}.` : ' Try again shortly.');
  }

  if (info.kind === 'quota_exceeded') {
    const period = info.window === 'day' ? "today's" : "this month's";
    const usage = info.used !== undefined && info.limit !== undefined
      ? ` You've used ${fmtNum(info.used)} of ${fmtNum(info.limit)} tokens.`
      : '';
    const resumes = back ? ` Access resumes ${back}.` : '';
    return `Your organization has used all of ${period} AI allowance.${usage}${resumes}` +
      ' You may ask the provider to raise your limits.';
  }

  // Unknown 429 shape — still better than silence.
  return info.detail
    || 'The AI service refused this request because a usage limit was reached. Try again later.';
}
